import type { UiSchema } from '@rjsf/utils';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';

import { setCaretToEndOfInput } from '@utils/dom-utils';

import type { BannersFormData } from './types';

/**
 * Create UI schema for authentication lock with numeric inputs.
 *
 * Generates RJSF UI schema that renders count and expires as numeric up / down
 * widgets.
 *
 * @returns UI schema with numeric widget configuration
 */
const createAuthLockUISchema = (): UiSchema => ({
  count: {
    'ui:widget': 'updown',
    'ui:options': { inputType: 'number' },
  },
  expires: {
    'ui:widget': 'updown',
    'ui:options': { inputType: 'number' },
  },
});

/**
 * Create UI schema for custom message with Markdown support. This configures
 * the title field to use the `monospace` font for better visibility of
 * formatting, and the message field to use a `textarea` widget for multi-line
 * Markdown content entry.
 *
 * @returns UI schema with text widget configurations
 */
const createCustomMessageUISchema = (): UiSchema => ({
  title: {
    'ui:options': { inputClassName: 'monospace-input' },
  },
  message: {
    'ui:widget': 'TextareaWidget',
  },
});

/**
 * Create UI schema for banners with color pickers and nullable object
 * handling. This processes the banner schema to:
 * - Apply custom field template to handle nullable top/bottom banners (this
 *   provides enable/disable toggles for each banner)
 * - Apply the `ColorPickerWidget` to the color fields
 *
 * @param schema - The JSON schema for the banners configuration
 * @param formData - Current form data to determine initial enabled state
 * @returns UI schema with nullable field templates and color picker widgets
 */
const createBannersUISchema = (schema: JSONSchema7): UiSchema => {
  const isColorField = (fieldName: string) =>
    fieldName.toLowerCase().includes('color') ||
    fieldName.toLowerCase().includes('background');

  const buildUISchema = (
    schemaObj: JSONSchema7 | JSONSchema7Definition,
    path: string[] = [],
  ): UiSchema => {
    if (!schemaObj || typeof schemaObj === 'boolean') return {};

    const result: UiSchema = {};
    const schemaTyped = schemaObj as JSONSchema7;

    if (schemaTyped.properties) {
      Object.entries(schemaTyped.properties).forEach(([key, propSchema]) => {
        const currentPath = [...path, key];

        // For the top and bottom banner fields, we need to handle them
        // specially since they are nullable objects
        if (path.length === 0 && (key === 'top' || key === 'bottom')) {
          // Mark these fields to use our custom banner section field
          result[key] = {
            'ui:field': 'BannerSectionField',
            // Checkboxes are unchecked by default
            'ui:options': {
              defaultEnabled: false,
              // Only show divider after the top banner
              showDivider: key === 'top',
              // Tell `ObjectFieldTemplate` not to add its own divider
              // (otherwise we get double dividers)
              suppressAutoDivider: true,
              // Hide the label for the field itself since we show it in the checkbox
              label: false,
            },
          };

          // We still need to process the nested properties for the color pickers
          if (typeof propSchema !== 'boolean' && propSchema.properties) {
            const nestedUISchema = buildUISchema(propSchema, currentPath);
            Object.entries(nestedUISchema).forEach(
              ([nestedKey, nestedValue]) => {
                result[key][nestedKey] = nestedValue;
              },
            );
          }
        }
        // Check if this field should use the color picker
        else if (
          typeof propSchema !== 'boolean' &&
          propSchema.type === 'string' &&
          isColorField(key)
        ) {
          result[key] = { 'ui:widget': 'ColorPickerWidget' };
        } else if (typeof propSchema !== 'boolean' && propSchema.properties) {
          // Recursively process nested objects
          const nestedUISchema = buildUISchema(propSchema, currentPath);
          if (Object.keys(nestedUISchema).length > 0) {
            result[key] = nestedUISchema;
          }
        }
      });
    }

    return result;
  };

  return buildUISchema(schema);
};

/**
 * Handle field focus when modal opens for specific configurations. Some
 * config types benefit from automatic focus management when the modal opens.
 * For example, the custom message `textarea` gets focus with the cursor
 * positioned at the end of existing content for easy continuation of editing
 * (we assume it's more likely that this information change than the title).
 *
 * @param configKey - The configuration key to handle focus for
 */
export const handleConfigSpecificFocus = (
  configKey: string | undefined,
): void => {
  if (configKey === 'customMessage') {
    setTimeout(() => {
      const textarea = document.getElementById(
        'root_message',
      ) as HTMLTextAreaElement;
      if (textarea) {
        setCaretToEndOfInput(textarea);
      }
    }, 0);
  }
};

/**
 * Create UI schema based on configuration type. This factory function returns
 * the appropriate UI schema for different config types. Each config type may
 * need special widgets or field configurations:
 * - `AuthenticationLock`: Numeric inputs that disable when feature is off
 * - `CustomMessage`: Monospace title and textarea for Markdown content
 * - `Banners`: Color pickers for color customization fields
 *
 * @param configKey - The configuration key to generate UI schema for
 * @param rjsfSchema - The prepared JSON schema
 * @param formData - Current form data for context-aware UI configuration
 * @returns Appropriate UI schema for the config type
 */
export const createUISchemaForConfig = (
  configKey: string | undefined,
  rjsfSchema: JSONSchema7,
  // formData: AnyConfigValue | undefined | null,
): UiSchema => {
  switch (configKey) {
    case 'authenticationLock':
      return createAuthLockUISchema();
    case 'customMessage':
      return createCustomMessageUISchema();
    case 'banners':
      return createBannersUISchema(rjsfSchema);
    default:
      return {};
  }
};

/**
 * Clean custom links data, preserving partial entries for validation. For the
 * links array, preserve items that have at least one non-empty field. This
 * allows validation to report missing required fields rather than silently
 * removing incomplete entries.
 *
 * Note: This function handles both the full `CustomLinksFormData` object and
 * nested arrays during recursive cleaning operations.
 *
 * @param formData - The form data to clean (can be partial during processing)
 * @param fieldPath - The current path in the data tree
 * @returns Cleaned data or `undefined` if empty
 */
export const cleanCustomLinksFormData = (
  formData: unknown,
  fieldPath: string[],
): unknown => {
  // Handle null/undefined
  if (formData === null || formData === undefined) {
    return undefined;
  }

  // Handle the links array specially to preserve partial entries
  if (
    fieldPath.length === 1 &&
    fieldPath[0] === 'links' &&
    Array.isArray(formData)
  ) {
    const cleanedArray = formData
      .map(item => {
        if (!item || typeof item !== 'object') return undefined;

        const cleanedItem: Record<string, unknown> = {};
        let hasAnyValue = false;

        Object.entries(item).forEach(([key, value]) => {
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed) {
              cleanedItem[key] = trimmed;
              hasAnyValue = true;
            } else {
              // Preserve empty string for validation to detect missing fields
              cleanedItem[key] = '';
            }
          } else if (value !== null && value !== undefined) {
            cleanedItem[key] = value;
            hasAnyValue = true;
          }
        });

        // Only return the item if it has at least one non-empty value
        return hasAnyValue ? cleanedItem : undefined;
      })
      .filter(item => item !== undefined);

    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  // For non-links fields, return the data as-is for default cleaning
  return formData;
};

/**
 * Clean banner data by removing empty fields and trimming strings. This
 * function completely replaces generic cleaning for banner data. It performs
 * standard cleaning operations like trimming strings and removing empty
 * objects. Since `banners` is nullable, the entire object will be `null` if
 * disabled via the main enable/disable checkbox in the UI.
 *
 * @param formData - The banner data from the form
 * @returns Cleaned banner data or `undefined` if empty
 */
export const cleanBannersFormData = (
  formData: BannersFormData,
): BannersFormData => {
  // Recursive helper to clean any value
  const cleanValue = (value: unknown, path: string[] = []): unknown => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return undefined;
    }

    // Handle strings
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      const cleaned = value
        .map((item, idx) => cleanValue(item, [...path, String(idx)]))
        .filter(item => item !== undefined);
      return cleaned.length > 0 ? cleaned : undefined;
    }

    // Handle objects
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const cleaned: Record<string, unknown> = {};
      let hasValidFields = false;

      // Check if this is a banner object (top or bottom)
      const isBannerObject =
        path.length === 1 && (path[0] === 'top' || path[0] === 'bottom');

      for (const [key, val] of Object.entries(obj)) {
        const cleanedVal = cleanValue(val, [...path, key]);
        if (cleanedVal !== undefined) {
          cleaned[key] = cleanedVal;
          hasValidFields = true;
        }
      }

      // Special case: preserve empty banner objects (indicates that the banner
      // is enabled but not yet configured). This allows the checkbox to remain
      // checked when a banner is first enabled.
      if (isBannerObject && !hasValidFields) {
        return {};
      }

      return hasValidFields ? cleaned : undefined;
    }

    // Return other types as-is
    return value;
  };

  return cleanValue(formData) as BannersFormData;
};
